import { HORIZONTAL_SIDES, VERTICAL_SIDES } from 'ContentUtils/constants/Border';

// Requires verticalSide and/or horizontalSide

export const isValidAlphaDec = alpha => {
  if (isNaN(alpha) || typeof alpha !== 'number' || alpha < 0 || alpha > 1) {
    return false;
  }
  return true;
};
export const isValidAlphaInt = alpha => {
  const isNotANumericAlpha = isNaN(alpha) || typeof alpha !== 'number';
  const isValidAlphaRange = alpha >= 0 && alpha <= 100;
  return alpha == null || !isNotANumericAlpha && isValidAlphaRange;
};
export const isValidRGB = colorChannels => {
  const rgb = ['r', 'g', 'b'];
  // The RGB values all have the same valid range, check if any one of them is bad
  const hasValidRGBValues = rgb.every(channel => {
    if (isNaN(colorChannels[channel]) || typeof colorChannels[channel] !== 'number' || colorChannels[channel] < 0 || colorChannels[channel] > 255) {
      return false;
    }
    return true;
  });
  // If there are no bad RGB values and we made is this far, than this is a valid RGBA object
  if (hasValidRGBValues) {
    return true;
  } else {
    return false;
  }
};
export const isValidRGBA = colorChannels => {
  return isValidAlphaDec(colorChannels.a) && isValidRGB(colorChannels);
};
export const isValidRgbString = colorString => {
  return /^rgb\((\d{1,3}%?),\s*(\d{1,3}%?),\s*(\d{1,3}%?)\)$/i.test(colorString);
};
export const isValidRgbaString = colorString => {
  return /^rgba\((\d{1,3}%?),\s*(\d{1,3}%?),\s*(\d{1,3}%?),\s*(\d*(?:\.\d+)?)\)$/i.test(colorString);
};

// Just checks that string 0-6 hex digits
// Returns true for partial hex codes or codes missing the #
export const isHexString = color => typeof color === 'string' && Boolean(color) && /^#?[0-9A-Fa-f]{0,6}$/.test(color);

// Checks that if input is a string, that it's an actually valid 3 or 6-digit hex code with a #
export const isValidHex = hex => {
  return hex == null || /^#([0-9A-F]{6}|[0-9A-F]{3})$/i.test(hex);
};
export const isValidGradientDirection = sideOrCornerObject => {
  const {
    verticalSide,
    horizontalSide
  } = sideOrCornerObject;
  if (!verticalSide && !horizontalSide) {
    console.warn('Gradient sideOrCorner key must specify the keys "verticalSide" and/or "horizonalSide"');
    return false;
  }
  if (verticalSide && verticalSide !== VERTICAL_SIDES.TOP && verticalSide !== VERTICAL_SIDES.BOTTOM) {
    return false;
  }
  if (horizontalSide && horizontalSide !== HORIZONTAL_SIDES.LEFT && horizontalSide !== HORIZONTAL_SIDES.RIGHT) {
    return false;
  }
  return true;
};
export const convertColorBitToHex = color => {
  let hex = Number(color).toString(16);
  if (hex.length < 2) hex = `0${hex}`;
  return hex;
};
export const convertRgbToHex = ({
  r,
  g,
  b
}) => {
  if (r == null || g == null || b == null) return null;
  const red = convertColorBitToHex(r);
  const green = convertColorBitToHex(g);
  const blue = convertColorBitToHex(b);
  const hex = `#${red}${green}${blue}`;
  return isValidHex(hex) ? hex : null;
};
const DEFAULT_ALPHA_INT = 100;
export const convertAlphaDecToInt = alphaDec => {
  const alphaInt = typeof alphaDec === 'number' ? alphaDec * DEFAULT_ALPHA_INT : DEFAULT_ALPHA_INT;
  return isValidAlphaInt(alphaInt) ? alphaInt : DEFAULT_ALPHA_INT;
};
const DEFAULT_ALPHA_DEC = 1.0;
export const convertAlphaIntToDec = alphaInt => {
  if (alphaInt === null || typeof alphaInt !== 'number') {
    return DEFAULT_ALPHA_DEC;
  }
  const alphaDec = alphaInt / DEFAULT_ALPHA_INT;
  return isValidAlphaDec(alphaDec) ? alphaDec : DEFAULT_ALPHA_DEC;
};
export const convertRgbaToHexAndAlphaInt = ({
  r,
  g,
  b,
  a = 1.0
}) => {
  return {
    hex: convertRgbToHex({
      r,
      g,
      b
    }),
    alpha: convertAlphaDecToInt(a)
  };
};
const EMPTY_RGB_OBJECT = {
  r: null,
  b: null,
  g: null
};
const getRbgNumberArray = rgbString => {
  var _rgbString$split$1$sp, _rgbString$split$;
  const rgbStringArray = (_rgbString$split$1$sp = (_rgbString$split$ = rgbString.split('(')[1]) === null || _rgbString$split$ === void 0 || (_rgbString$split$ = _rgbString$split$.split(')')[0]) === null || _rgbString$split$ === void 0 ? void 0 : _rgbString$split$.split(',')) !== null && _rgbString$split$1$sp !== void 0 ? _rgbString$split$1$sp : [];
  return rgbStringArray.map(stringNum => parseFloat(stringNum));
};
export const convertRgbStringToRgbObject = rgbString => {
  var _rgbNumberArray$, _rgbNumberArray$2, _rgbNumberArray$3;
  if (typeof rgbString !== 'string') {
    return EMPTY_RGB_OBJECT;
  }
  const rgbNumberArray = getRbgNumberArray(rgbString);
  const rgbObject = {
    r: (_rgbNumberArray$ = rgbNumberArray[0]) !== null && _rgbNumberArray$ !== void 0 ? _rgbNumberArray$ : null,
    g: (_rgbNumberArray$2 = rgbNumberArray[1]) !== null && _rgbNumberArray$2 !== void 0 ? _rgbNumberArray$2 : null,
    b: (_rgbNumberArray$3 = rgbNumberArray[2]) !== null && _rgbNumberArray$3 !== void 0 ? _rgbNumberArray$3 : null
  };
  return isValidRGB(rgbObject) ? rgbObject : EMPTY_RGB_OBJECT;
};
export const convertRgbaStringToRgbaObject = rgbaString => {
  var _rgbaNumberArray$, _rgbaNumberArray$2, _rgbaNumberArray$3;
  if (typeof rgbaString !== 'string') {
    return Object.assign({}, EMPTY_RGB_OBJECT, {
      a: null
    });
  }
  const rgbaNumberArray = getRbgNumberArray(rgbaString);
  const rgbaObject = {
    r: (_rgbaNumberArray$ = rgbaNumberArray[0]) !== null && _rgbaNumberArray$ !== void 0 ? _rgbaNumberArray$ : null,
    g: (_rgbaNumberArray$2 = rgbaNumberArray[1]) !== null && _rgbaNumberArray$2 !== void 0 ? _rgbaNumberArray$2 : null,
    b: (_rgbaNumberArray$3 = rgbaNumberArray[2]) !== null && _rgbaNumberArray$3 !== void 0 ? _rgbaNumberArray$3 : null,
    a: typeof rgbaNumberArray[3] === 'number' ? rgbaNumberArray[3] : 1
  };
  return isValidRGBA(rgbaObject) ? rgbaObject : Object.assign({}, EMPTY_RGB_OBJECT, {
    a: null
  });
};
export const convertHexAndAlphaToRgbaObject = (hex, alpha = 1) => {
  if (!isValidHex(hex) || !hex) return Object.assign({}, EMPTY_RGB_OBJECT, {
    a: null
  });
  hex = hex.replace('#', '');

  // 3 characters is a valid hex, double it to 6 characters for the sake of
  // computed rgba values if needed. Changes "abc" to "aabbcc"
  if (hex.length === 3) {
    const sixDigitHex = hex.split('').reduce((accumulator, currentValue) => {
      return accumulator + currentValue + currentValue;
    }, '');
    hex = sixDigitHex;
  }
  const rgba = {
    r: parseInt(hex.substring(0, 2), 16),
    g: parseInt(hex.substring(2, 4), 16),
    b: parseInt(hex.substring(4, 6), 16),
    a: alpha
  };
  return isValidRGBA(rgba) ? rgba : Object.assign({}, EMPTY_RGB_OBJECT, {
    a: null
  });
};
export const convertHexAndAlphaIntToRgbaObject = (hex, opacity = 100) => {
  return convertHexAndAlphaToRgbaObject(hex, convertAlphaIntToDec(opacity));
};

/**
 * Adjusts the brightness of a hex color by a given offset
 * @param hexColor - The hex color string (e.g., "#3A539B" or "#abc")
 * @param brightnessOffset - Positive number to lighten, negative to darken
 * @returns The adjusted hex color string
 */
export function adjustColorBrightness(hexColor, brightnessOffset) {
  if (!hexColor || hexColor.trim() === '') {
    return hexColor;
  }
  const rgbHex = convertHexAndAlphaToRgbaObject(hexColor);
  if (!rgbHex || !isValidRGBA(rgbHex)) {
    return hexColor;
  }
  rgbHex.r = Math.min(255, Math.max(0, rgbHex.r + brightnessOffset));
  rgbHex.g = Math.min(255, Math.max(0, rgbHex.g + brightnessOffset));
  rgbHex.b = Math.min(255, Math.max(0, rgbHex.b + brightnessOffset));
  return convertRgbToHex(rgbHex) || hexColor;
}

// Luma coefficients sourced from https://en.wikipedia.org/wiki/Rec._709#Luma_coefficients
const RED_LUMA_COEFFICIENT = 0.2126;
const GREEN_LUMA_COEFFICIENT = 0.7152;
const BLUE_LUMA_COEFFICIENT = 0.0722;
export const getPerceivedBrightness = hexColor => {
  if (!hexColor || hexColor.trim() === '' || !isValidHex(hexColor)) {
    return null;
  }
  const rgbHex = convertHexAndAlphaToRgbaObject(hexColor);
  if (!rgbHex || !isValidRGBA(rgbHex)) {
    return null;
  }
  const {
    r,
    g,
    b
  } = rgbHex;
  return RED_LUMA_COEFFICIENT * r + GREEN_LUMA_COEFFICIENT * g + BLUE_LUMA_COEFFICIENT * b;
};

// Thresholds sourced from https://stackoverflow.com/a/12043228
// Used same light threshold but increased dark a bit to ensure better contrast between main color and darker shades
// TODO: go over these and shade increments with Sakura/Jason
const DARK_THRESHOLD = 50;
const LIGHT_THRESHOLD = 128;

// Temp - allow overriding light/dark thresholds while we figure out what they should be
export const isDark = (hexColor, darkThreshold = DARK_THRESHOLD) => {
  const perceivedBrightness = getPerceivedBrightness(hexColor);
  return perceivedBrightness !== null && perceivedBrightness < darkThreshold;
};
export const isLight = (hexColor, lightThreshold = LIGHT_THRESHOLD) => {
  const perceivedBrightness = getPerceivedBrightness(hexColor);
  return perceivedBrightness !== null && perceivedBrightness > lightThreshold;
};